import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { buildNameLikeSelectOptions } from '@/components/ui/multi-select-utils.js';
import { useAllSchools } from '@/lib/api-call-hooks.js';
import { getUsers, updateUserRoles } from '@/lib/api-calls.js';
import { QueryKeys } from '@/lib/queries.js';
import { nonCoreUserRolesFormSchema } from '@/routes/_layout/users/-components/form-schema.js';
import { NonCoreUserRolesForm } from '@/routes/_layout/users/-components/non-core-user-roles-form.js';
import { distinct, isNotNullish, isSchoolAdminRole, isSchoolContactRole, roleHash } from '@airreading/common';
import { UserRoleAssignmentOptions, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

export interface ManageNonCoreRolesProps {
  user: UserWithRolesAndProfiles;
  trigger: ReactNode;
}

const formSchema = nonCoreUserRolesFormSchema;

export function ManageNonCoreRoles({ user, trigger }: ManageNonCoreRolesProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: updateUserRoles,
  });
  const { data: allSchools, isPending: isLoadingSchools } = useAllSchools();
  const allSchoolsOptions = allSchools.map(buildNameLikeSelectOptions);

  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { isSchoolAdmin, adminSchools = [], isSchoolContact, contactSchools = [] } = formData;
    const adminSchoolIds = adminSchools.map(({ value }) => value).filter(isNotNullish);
    const contactSchoolIds = contactSchools.map(({ value }) => value).filter(isNotNullish);

    if (isSchoolAdmin && !adminSchoolIds.length) {
      toast({
        title: 'Please select a school to assign school admin role',
      });
      return;
    }

    if (isSchoolContact && !contactSchoolIds.length) {
      toast({
        title: 'Please select a school to assign school contact role',
      });
      return;
    }

    const schoolAdmins: UserRoleAssignmentOptions = isSchoolAdmin
      ? adminSchoolIds.map((schoolId) => ({
          role: 'school_admin',
          schoolId,
        }))
      : [];
    const schoolContacts: UserRoleAssignmentOptions = isSchoolContact
      ? contactSchoolIds.map((schoolId) => ({
          role: 'school_contact',
          schoolId,
        }))
      : [];

    // The roles selected in this form fully replace the user's existing school admin /
    // school contact roles, so schools removed in the form are not carried over.
    const otherRoles = user.userRoles.filter((role) => !isSchoolAdminRole(role) && !isSchoolContactRole(role));

    const updatedRoles: UserRoleAssignmentOptions = [...otherRoles, ...schoolAdmins, ...schoolContacts].filter(
      distinct(roleHash),
    );

    return mutateAsync({
      userId: user.id,
      roles: updatedRoles.map(({ role, schoolId }) => ({ role, schoolId })),
    })
      .then(() => {
        toast({
          title: 'User roles updated successfully',
        });
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
        queryClient.invalidateQueries(getUsers());
        closeModal();
      })
      .catch(() => {
        toast({
          title: 'User roles update failed',
        });
      });
  };

  return (
    <FormModal
      modalTitle="Manage roles"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={{
        isSchoolAdmin: user.userRoles.some(isSchoolAdminRole),
        adminSchools: allSchoolsOptions.filter((option) =>
          user.userRoles.some((role) => isSchoolAdminRole(role) && role.schoolId === option.value),
        ),
        isSchoolContact: user.userRoles.some(isSchoolContactRole),
        contactSchools: allSchoolsOptions.filter((option) =>
          user.userRoles.some((role) => isSchoolContactRole(role) && role.schoolId === option.value),
        ),
      }}
      dialogContentProps={{ className: 'max-w-2xl' }}
    >
      {({ form }) => {
        return (
          <div className="grid grid-cols-2 gap-2 py-4">
            {isLoadingSchools ? (
              <div>
                <LoaderIcon />
              </div>
            ) : (
              <>
                <NonCoreUserRolesForm form={form} />
              </>
            )}
          </div>
        );
      }}
    </FormModal>
  );
}
